import React, {
  useContext,
} from 'react';
import clsx from 'clsx';
import {
  ServiceAllocation,
} from 'pages/Dashboard/pages/Billing/types';
import {
  useFormContext,
} from 'react-hook-form';
import Controller from 'core/components/FormController';
import Select from 'core/components/Select';
import CurrencyTextField from 'core/components/CurrencyTextField';
import AdjustmentReasonCodeLookup from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/AdjustmentReasonCodeLookup';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import {
  ClaimAdjustmentGroupCodeSettingDTO,
  ClaimAdjustmentReasonCodeDTO,
  ClaimAdjustmentViewDTO,
} from 'dtos';
import {
  paymentsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  useClaimSettings,
} from 'pages/Dashboard/pages/Billing/hooks';
import NumberTextField from 'core/components/NumberTextField';
import EOBCodesPopover from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/EOBCodesPopover';
import {
  formatMoney,
} from '@populateui/utils';
import {
  AdjustmentExceptionMap,
  getAdjustmentStyleForCode,
} from 'pages/Dashboard/pages/Billing/Payments/utils';
import {
  AdjustmentRelatedField,
} from 'pages/Dashboard/pages/Billing/Payments/constants';
import {
  WizardContext,
} from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/wizardContext';

export type ReasonCode = Pick<ClaimAdjustmentReasonCodeDTO, 'description'> & {
  code?: string;
};

export type ClaimAdjustment = Pick<ClaimAdjustmentViewDTO,
  'adjustedItemId'
  | 'claimAdjustmentId'
  | 'claimAdjustmentGroupId'
  | 'canMoveToAutoWriteOff'
  | 'canMoveBackToInsuranceAdjustments'
> & {
  groupCode?: ClaimAdjustmentGroupCodeSettingDTO | null;
  reasonCode?: ReasonCode | null;
  amount?: string;
  quantity?: string;
  serviceCode?: string;
  effectiveBucket?: AdjustmentRelatedField;
  defaultBucket?: AdjustmentRelatedField;
};

export type EOBAdjustmentCodeParams = {
  index: number;
  adjustments: ClaimAdjustment[];
  data: ServiceAllocation;
};

type Props = {
  data: ServiceAllocation;
  index: number;
  readonly?: boolean;
};

const defaultValues: ClaimAdjustment = Object.freeze({
  groupCode: null,
  reasonCode: null,
  amount: '',
  quantity: '',
});

const formKey = 'adjustments';

type EOBAdjustmentCodesProps = {
  index: number;
};

export function EOBAdjustmentCodes({ index }: EOBAdjustmentCodesProps) {
  const { adjustmentGroupCodeSettings } = useClaimSettings();
  const { control, setValue } = useFormContext();

  const resetAdjustmentBuckets = () => {
    setValue(`${formKey}.${index}.defaultBucket`, undefined, { shouldDirty: true });
    setValue(`${formKey}.${index}.effectiveBucket`, undefined, { shouldDirty: true });
    setValue(`${formKey}.${index}.canMoveToAutoWriteOff`, undefined, { shouldDirty: true });
    setValue(`${formKey}.${index}.canMoveBackToInsuranceAdjustments`, undefined, { shouldDirty: true });
  };

  return (
    <div className="flex w-full items-center gap-2 text-xs">
      <Controller
        name={`${formKey}.${index}.groupCode`}
        control={control}
        rules={{ required: true }}
        render={({ field, fieldState: { invalid } }) => (
          <Wrapper
            label={index === 0 ? 'Group Code' : undefined}
            id="group-code"
            error={invalid}
            className="!min-w-[25%]"
            showErrorText={false}
          >
            <Select<ClaimAdjustmentGroupCodeSettingDTO>
              {...field}
              className="text-gray-500 !w-full"
              placeholder="Group Code"
              options={adjustmentGroupCodeSettings ?? []}
              renderValue={() => field?.value?.code ?? 'Select Group Code'}
              getLabel={(option: ClaimAdjustmentGroupCodeSettingDTO) => `${option?.code ?? ''} - ${option?.description ?? ''}`}
              getValue={(option: ClaimAdjustmentGroupCodeSettingDTO) => option?.code ?? ''}
              onChange={(value) => {
                field.onChange(value);
                resetAdjustmentBuckets();
              }}
            />
          </Wrapper>
        )}
      />
      <Controller
        name={`${formKey}.${index}.reasonCode`}
        control={control}
        rules={{ required: true }}
        render={({ field, fieldState: { invalid } }) => (
          <Wrapper
            label={index === 0 ? 'Reason Code' : undefined}
            id="adjustmentCode"
            error={invalid}
            showErrorText={false}
            className="!w-1/4"
          >
            <AdjustmentReasonCodeLookup
              {...field}
              id="adjustmentCodes"
              placeholder="Adjustment codes"
              onChange={(value) => {
                field.onChange(value);
                resetAdjustmentBuckets();
              }}
            />
          </Wrapper>
        )}
      />
      <Controller
        name={`${formKey}.${index}.amount`}
        control={control}
        rules={{ required: true }}
        render={({ field: { value, onChange, ...field }, fieldState: { invalid } }) => (
          <Wrapper
            label={index === 0 ? 'Amount' : undefined}
            id="amount"
            error={invalid}
            showErrorText={false}
            className="!w-1/4"
          >
            <CurrencyTextField
              {...field}
              allowNegative
              value={value}
              onChange={(updatedValue) => {
                if (updatedValue !== value) {
                  onChange(updatedValue);
                }
              }}
            />
          </Wrapper>
        )}
      />
      <Controller
        name={`${formKey}.${index}.quantity`}
        control={control}
        render={({ field, fieldState: { invalid } }) => (
          <Wrapper
            label={index === 0 ? 'Quantity' : undefined}
            id="quantity"
            error={invalid}
            showErrorText={false}
            className="!w-1/4"
          >
            <NumberTextField {...field} />
          </Wrapper>
        )}
      />
    </div>
  );
}

export function getAdjustmentLabel(
  item: ClaimAdjustment,
  adjustmentExceptionMap: AdjustmentExceptionMap = {},
) {
  const style = getAdjustmentStyleForCode(item, adjustmentExceptionMap);

  return (
    <div className="flex gap-1 justify-center items-center">
      <span className={clsx('font-bold', style.textClassName)}>
        {item?.groupCode?.code}
        -
        {item?.reasonCode?.code}
      </span>
      <span className={clsx('font-semibold', style.amountTextClassName)}>
        {formatMoney(Number(item?.amount ?? 0))}
      </span>
    </div>
  );
}

function getAdjustmentPillClassName(
  item: ClaimAdjustment,
  adjustmentExceptionMap: AdjustmentExceptionMap = {},
) {
  const style = getAdjustmentStyleForCode(item, adjustmentExceptionMap);

  return clsx(
    style.backgroundClassName,
    style.borderClassName,
    style.textClassName,
  );
}

export default function EOBAdjustmentCodePopover({
  index,
  data,
  readonly,
}: Props) {
  const { adjustmentExceptionMap } = useContext(WizardContext);

  return (
    <EOBCodesPopover<ClaimAdjustment>
      data={data?.adjustments ?? []}
      readonly={readonly}
      formKey={formKey}
      defaultValues={defaultValues}
      CodePopoverComponent={EOBAdjustmentCodes}
      getLabel={(item) => getAdjustmentLabel(item, adjustmentExceptionMap)}
      getPillClassName={(item) => getAdjustmentPillClassName(item, adjustmentExceptionMap)}
      getId={(item) => item?.claimAdjustmentId?.toString() ?? `${item?.groupCode?.code}-${item?.reasonCode?.code}`}
      onSubmit={(adjustments) => {
        paymentsEventChannel.emit('onChangeEOBAdjustmentCode', { index, adjustments, data });
      }}
      tooltip="Add Claim Adjustment Reason Code"
      max={4}
    />
  );
}
